import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InboxItemDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { relativeTime } from "@/lib/format";
import { Btn } from "@/design/primitives";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "@/pages/decisions/project-filter";

const URGENCY_META: Record<
  string,
  { label: string; color: string; border: string; bg: string }
> = {
  URGENT: {
    label: "KRITICKÉ",
    color: "var(--critical)",
    border: "color-mix(in srgb, var(--critical) 40%, transparent)",
    bg: "color-mix(in srgb, var(--critical) 4%, transparent)",
  },
  HIGH: {
    label: "VYSOKÉ",
    color: "var(--warning)",
    border: "color-mix(in srgb, var(--warning) 30%, transparent)",
    bg: "transparent",
  },
  NORMAL: {
    label: "NÁVRH",
    color: "var(--accent)",
    border: "color-mix(in srgb, var(--accent) 15%, transparent)",
    bg: "transparent",
  },
  LOW: {
    label: "NÍZKÉ",
    color: "var(--text-secondary)",
    border: "color-mix(in srgb, var(--accent) 12%, transparent)",
    bg: "transparent",
  },
};

/**
 * Mobilní Decision Inbox (p1e + p1g).
 *
 * Karta je **souhrn**, ne ovládací panel: rozhoduje se až v detailu (p7c/9).
 * Palec na telefonu je nepřesný a jedno klepnutí do seznamu by rozhodlo za
 * tebe; undo lišta na 8 s zůstává i tak.
 */
export function MobileDecisions() {
  const queryClient = useQueryClient();
  const inbox = useQuery({
    queryKey: ["inbox"],
    queryFn: () => api.decisionInbox(),
  });
  const [unblocked, setUnblocked] = useState(0);
  const [rawProjectFilter, setProjectFilter] = useState<string>(ALL_PROJECTS);
  /** Naposledy vyřešené rozhodnutí — po dobu 8 s nabízí undo (jednotapové UI se snadno překlikne). */
  const [undoId, setUndoId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolveMutation = useMutation({
    mutationFn: ({
      id,
      resolution,
    }: {
      id: string;
      resolution: string;
      kind?: string;
    }) => api.resolveDecision(id, { resolution }),
    onSuccess: (data, variables) => {
      setUnblocked((n) => n + data.unblockedCount);
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
      // schválený proposal vrátit nejde (vznikl task) — undo nenabízíme
      setOpenId(null);
      const approvedProposal =
        variables.kind === "PROPOSAL" && variables.resolution !== "REJECTED";
      if (undoTimer.current) clearTimeout(undoTimer.current);
      if (approvedProposal) {
        setUndoId(null);
      } else {
        setUndoId(variables.id);
        undoTimer.current = setTimeout(() => setUndoId(null), 8000);
      }
    },
  });

  const undoMutation = useMutation({
    mutationFn: (id: string) => api.reopenDecision(id),
    onSuccess: () => {
      setUndoId(null);
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
  });

  const allItems = (inbox.data?.items ?? []).filter(
    (i) => i.itemType === "DECISION",
  );
  const projectFilter = resolveProjectFilter(
    projectGroups(allItems),
    rawProjectFilter,
  );
  const items = filterByProject(allItems, projectFilter);

  const openItem = items.find((i) => i.id === openId) ?? null;
  if (openItem) {
    return (
      <MobileDecisionDetail
        item={openItem}
        index={items.indexOf(openItem) + 1}
        total={items.length}
        busy={resolveMutation.isPending}
        onBack={() => setOpenId(null)}
        onResolve={(resolution) =>
          resolveMutation.mutate({
            id: openItem.id,
            resolution,
            kind: openItem.decision?.kind,
          })
        }
      />
    );
  }

  return (
    <div className="af-animate-fade" data-testid="mobile-decisions">
      <div className="mx-1 mb-3 flex items-baseline justify-between">
        <span
          className="af-display text-[16px] font-light"
          style={{ color: "var(--text-primary)" }}
        >
          {cs.mobile.inboxTitle}
        </span>
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.mobile.inboxWaiting(items.length)}
        </span>
      </div>

      <ProjectFilterTabs
        items={allItems}
        value={projectFilter}
        onChange={setProjectFilter}
        className="mx-1 mb-3"
        testIdPrefix="mobile-inbox"
      />

      {undoId ? (
        <div
          className="mb-2.5 flex items-center justify-between gap-3 px-3.5 py-2.5"
          style={{
            borderRadius: "var(--radius-box)",
            border:
              "1px solid color-mix(in srgb, var(--positive) 30%, transparent)",
          }}
          data-testid="mobile-undo-bar"
        >
          <span className="text-[12px]" style={{ color: "var(--positive)" }}>
            ✓ {cs.decisions.resolvedToast(0)}
          </span>
          <button
            type="button"
            className="cursor-pointer whitespace-nowrap px-3 py-1.5 text-[11px] font-bold tracking-[0.1em] disabled:opacity-40"
            style={{
              borderRadius: "var(--radius-box)",
              border:
                "1px solid color-mix(in srgb, var(--warning) 45%, transparent)",
              color: "var(--warning)",
            }}
            disabled={undoMutation.isPending}
            onClick={() => undoMutation.mutate(undoId)}
            data-testid="mobile-undo"
          >
            ↩ {cs.decisions.undo}
          </button>
        </div>
      ) : null}

      {items.map((item) => (
        <DecisionCard
          key={item.id}
          item={item}
          onOpen={() => setOpenId(item.id)}
        />
      ))}

      {items.length === 0 && !inbox.isLoading ? (
        <div
          className="mt-5 px-5 py-8 text-center"
          style={{
            border:
              "1px dashed color-mix(in srgb, var(--positive) 30%, transparent)",
          }}
          data-testid="mobile-inbox-empty"
        >
          <div className="af-card-title" style={{ color: "var(--positive)" }}>
            {cs.mobile.allDecided}
          </div>
          <div
            className="mt-2 whitespace-pre-line text-[11px] leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {cs.mobile.allDecidedNote(unblocked)}
          </div>
        </div>
      ) : null}
    </div>
  );

  function DecisionCard({
    item,
    onOpen,
  }: {
    item: InboxItemDTO;
    onOpen: () => void;
  }) {
    const meta = URGENCY_META[item.urgency] ?? URGENCY_META.NORMAL!;
    const decision = item.decision;
    const options = decision?.alternatives.length ?? 0;
    return (
      <button
        type="button"
        onClick={onOpen}
        className="af-animate-fade mb-2.5 block w-full p-3.5 text-left"
        style={{
          borderRadius: "var(--radius-card)",
          border: `1px solid ${meta.border}`,
          background: meta.bg === "transparent" ? "var(--surface)" : meta.bg,
        }}
        data-testid="mobile-decision-card"
      >
        <div className="af-mono mb-1.5 flex flex-wrap justify-between gap-x-2 gap-y-1 text-[10px] tracking-[0.14em]">
          <span style={{ color: meta.color }}>
            {meta.label}
            {decision?.blocksProject
              ? ` · ${cs.decisions.blocksProjectTag}`
              : ""}
          </span>
          <span style={{ color: "var(--text-tertiary)" }}>
            {item.projectName?.toUpperCase() ?? "FACTORY"} ·{" "}
            {relativeTime(item.createdAt)}
          </span>
        </div>
        <div className="af-card-title mb-2 leading-snug">{item.title}</div>
        {decision?.recommendation ? (
          <div
            className="mb-2 px-3 py-2 text-[12px] leading-relaxed"
            style={{
              borderLeft:
                "2px solid color-mix(in srgb, var(--positive) 40%, transparent)",
              background: "var(--positive-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            <span style={{ color: "var(--positive)" }}>AI:</span>{" "}
            {decision.recommendation}
          </div>
        ) : null}
        {item.blocksSummary ? (
          <div className="mb-2 text-[12px]" style={{ color: "var(--warning)" }}>
            ⛔ {item.blocksSummary}
          </div>
        ) : null}
        {/* karta je souhrn — rozhoduje se v detailu (p7c/9) */}
        <div className="flex items-center justify-between gap-2 text-[12px]">
          <span style={{ color: "var(--text-tertiary)" }}>
            {options ? cs.mobile.optionsCount(options) : cs.mobile.ownSolution}
          </span>
          <span
            className="af-mono text-[11px]"
            style={{ color: "var(--accent)" }}
          >
            {cs.mobile.decide} →
          </span>
        </div>
      </button>
    );
  }
}

/**
 * Doporučená varianta se pozná z textu doporučení, ne z pořadí — AI píše
 * „Doporučuji Stripe", a to nemusí být první alternativa v seznamu.
 */
function isRecommended(
  recommendation: string | undefined,
  label: string,
): boolean {
  if (!recommendation || !label) return false;
  return recommendation.toLowerCase().includes(label.toLowerCase());
}

/**
 * Detail rozhodnutí na mobilu (p1g). Kontext, možnosti klepnutím a akce dole
 * — palcem dosažitelné, a hlavně až tady, ne v seznamu.
 */
function MobileDecisionDetail({
  item,
  index,
  total,
  busy,
  onBack,
  onResolve,
}: {
  item: InboxItemDTO;
  index: number;
  total: number;
  busy: boolean;
  onBack: () => void;
  onResolve: (resolution: string) => void;
}) {
  const meta = URGENCY_META[item.urgency] ?? URGENCY_META.NORMAL!;
  const decision = item.decision;
  const [choice, setChoice] = useState<string | null>(null);
  const [note, setNote] = useState("");

  return (
    <div className="af-animate-fade pb-24" data-testid="mobile-decision-detail">
      <div className="mb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex h-11 w-11 items-center justify-center text-[16px]"
          style={{ color: "var(--text-secondary)" }}
          aria-label={cs.common.back}
          data-testid="mobile-decision-back"
        >
          ←
        </button>
        <span className="af-card-title">{cs.mobile.inboxTitle}</span>
        <span
          className="af-mono ml-auto text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {index} / {total}
        </span>
      </div>

      <div className="af-mono mb-2 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.14em]">
        <span
          className="px-2.5 py-1"
          style={{
            borderRadius: "var(--radius-pill)",
            background: "var(--critical-bg)",
            color: meta.color,
          }}
        >
          {meta.label}
        </span>
        {decision?.blocksProject ? (
          <span
            className="px-2.5 py-1"
            style={{
              borderRadius: "var(--radius-pill)",
              background: "var(--critical-bg)",
              color: "var(--critical)",
            }}
          >
            {cs.decisions.blocksProjectTag}
          </span>
        ) : null}
        <span style={{ color: "var(--text-tertiary)" }}>
          {item.projectName?.toUpperCase() ?? "FACTORY"} ·{" "}
          {relativeTime(item.createdAt)}
        </span>
      </div>

      <h1
        className="af-display mb-4 text-[20px] font-bold leading-snug"
        style={{ color: "var(--text-primary)" }}
      >
        {item.title}
      </h1>

      {decision?.context ? (
        <div
          className="mb-3 p-3.5"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-default)",
            background: "var(--surface)",
          }}
        >
          <div className="af-label-dim mb-1.5">{cs.decisions.context}</div>
          <p
            className="text-[13px] leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {decision.context}
          </p>
        </div>
      ) : null}

      {decision && decision.alternatives.length > 0 ? (
        <>
          <div className="af-label-dim mb-2">{cs.mobile.optionsTap}</div>
          <div className="mb-3 flex flex-col gap-2">
            {decision.alternatives.map((alt) => {
              const on = choice === alt.key;
              return (
                <button
                  key={alt.key}
                  type="button"
                  onClick={() => setChoice(alt.key)}
                  className="flex w-full items-start gap-3 p-3.5 text-left"
                  style={{
                    borderRadius: "var(--radius-box)",
                    border: `1px solid ${on ? "var(--positive-border)" : "var(--border-default)"}`,
                    background: on
                      ? "var(--positive-subtle)"
                      : "var(--surface)",
                  }}
                  data-testid={`mobile-alt-${alt.key}`}
                >
                  <span
                    style={{
                      color: on ? "var(--positive)" : "var(--text-tertiary)",
                    }}
                  >
                    {on ? "◉" : "○"}
                  </span>
                  <span className="min-w-0">
                    <span
                      className="block text-[13px] font-semibold"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {alt.label}
                      {isRecommended(decision.recommendation, alt.label) ? (
                        <span
                          className="af-mono ml-2 text-[10px]"
                          style={{ color: "var(--positive)" }}
                        >
                          {cs.mobile.recommended}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="mt-0.5 block text-[12px]"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {alt.impact}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      <div className="af-label-dim mb-1.5">{cs.decisions.customLabel}</div>
      <textarea
        className="mb-3 w-full resize-y p-3 text-[13px] outline-none"
        style={{
          borderRadius: "var(--radius-control)",
          border: "1px solid var(--border-field)",
          background: "var(--surface-inset)",
          color: "var(--text-primary)",
          minHeight: 72,
        }}
        placeholder={cs.decisions.customPlaceholder}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="mobile-decision-note"
      />

      {/* akce dole — v dosahu palce, ne u horního okraje */}
      <div
        className="fixed inset-x-0 bottom-[64px] z-30 flex gap-2 px-3.5 py-3"
        style={{
          background: "var(--surface-overlay)",
          borderTop: "1px solid var(--border-default)",
          backdropFilter: "var(--surface-blur)",
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        }}
      >
        <Btn
          variant="primary"
          size="lg"
          className="flex-1"
          disabled={busy || (!choice && !note.trim())}
          onClick={() => onResolve(choice ?? "CUSTOM")}
          data-testid="mobile-approve"
        >
          {choice ? cs.mobile.decideChosen : cs.decisions.customSubmit}
        </Btn>
        <Btn
          variant="danger"
          size="lg"
          disabled={busy}
          onClick={() => onResolve("REJECTED")}
          data-testid="mobile-reject"
        >
          ✕
        </Btn>
      </div>
    </div>
  );
}
